/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { createSelector } from '@reduxjs/toolkit';
import { useFormik } from 'formik';
import { getCities, getStates, getZones } from 'live-slices/address/thunk';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Col, Input, Label, ListGroup, ListGroupItem } from 'reactstrap';
import * as Yup from "yup";
import TPLMapComponent from "Components/Common/TPLMap";
import { AddressModel } from './BuypassAddressSelector';

const AddressFormBox = ({ addressObject, onAddressUpdate }: { addressObject?: AddressModel, onAddressUpdate: (address: any) => void }) => {
    const dispatch: any = useDispatch();
    const [userCoordinates, setUserCoordinates] = useState<{ lat: number; lng: number } | null>(null);
    const validationSchema = Yup.object({
        streetAddress: Yup.string()
            .required("Address details are required")
            .min(5, "Address must be at least 5 characters long"),
        regionState: Yup.string()
            .required("Region/City/District is required"),
        shopName: Yup.string()
            .required("Shop Name is required"),
        plazaMarketName: Yup.string()
            .required("Plaza Market Name is required"),
        shopNumber: Yup.string()
            .required("Shop Number is required"),
    });
    const stateDataSelector = createSelector(
        (state: any) => state?.Address,
        (states) => states?.states
    );
    const cityDataSelector = createSelector(
        (state: any) => state?.Address,
        (cities) => cities?.cities
    );

    const zoneDataSelector = createSelector(
        (state: any) => state?.Address,
        (zones) => zones?.zones
    );
    const stateList = useSelector(stateDataSelector);
    const citiesList = useSelector(cityDataSelector);
    const zonesList = useSelector(zoneDataSelector);

    useEffect(() => {
        dispatch(getStates());
    }, [dispatch]);

    useEffect(() => {
        if (stateList.length) {
            drawAddressSelection(addressObject);
        }
    }, [stateList])

    useEffect(() => {
        if (citiesList.length) {
            drawAddressSelection(addressObject);
        }
    }, [citiesList])

    useEffect(() => {
        if (zonesList.length) {
            drawAddressSelection(addressObject);
        }
    }, [zonesList])

    useEffect(() => {
        if (addressObject) {
            if (addressObject.streetAddress) {
                formik.setFieldValue('streetAddress', addressObject.streetAddress);
            }
            let regionState = '';
            if (addressObject.stateId) {
                regionState = addressObject.state;
                formik.setFieldValue('state', addressObject.state);
                formik.setFieldValue('stateId', addressObject.stateId);
                dispatch(getCities(addressObject.stateId))
                if (addressObject.city) {
                    regionState = `${regionState} / ${addressObject.city}`;
                    formik.setFieldValue('city', addressObject.city);
                    formik.setFieldValue('cityId', addressObject.cityId);
                    dispatch(getZones(addressObject.cityId))
                    if (addressObject.zone) {
                        formik.setFieldValue('zone', addressObject.zone);
                        formik.setFieldValue('zoneId', addressObject.zoneId);
                        regionState = `${regionState} / ${addressObject.zone}`;
                    }
                }
            }
            formik.setFieldValue('regionState', regionState);
            formik.setFieldValue('plazaMarketName', addressObject.plazaMarketName)
            formik.setFieldValue('shopName', addressObject.shopName)
            formik.setFieldValue('shopNumber', addressObject.shopNumber)
            formik.setFieldValue('isPlaza', addressObject.isPlaza)
            formik.setFieldValue('coordinates', addressObject.coordinates)
            if(addressObject.coordinates && addressObject.coordinates.length>0){
                formik.setFieldValue('latitude', addressObject.coordinates[1])
                formik.setFieldValue('longitude', addressObject.coordinates[0])
                setUserCoordinates({ lat: addressObject.coordinates[1], lng: addressObject.coordinates[0] })
            }

            drawAddressSelection(addressObject);
        }
    }, [addressObject])



    const formik = useFormik({
        initialValues: {
            zone: "",
            zoneId: "",
            city: "",
            cityId: "",
            state: "",
            stateId: "",
            streetAddress: "",
            regionState: "",
            isPlaza: true,
            plazaMarketName: "",
            shopName: "",
            shopNumber: "",
            longitude: 0,
            latitude: 0, // Initial value for latitude
            coordinates: [] as number[]
        },
        validationSchema,
        onSubmit: (values) => {
            const addressModel = new AddressModel();
            addressModel.coordinates = [values.latitude, values.longitude];
        },
    });

    const handleCoordinatesChange = (lat: number, lng: number) => {
        formik.values.latitude = lat;
        formik.values.longitude = lng;
        formik.setFieldValue('latitude', lat);
        formik.setFieldValue('longitude', lng);

        sendUpdate()
    };

    const drawAddressSelection = (address: AddressModel | any = formik.values) => {
        let drawList = [];
        drawList.push({
            list: stateList,
            next: "city",
            selection: address && address.stateId ? address.stateId : '',
        });
        if (address) {
            if (address.stateId) {
                drawList.push({
                    list: citiesList.filter((x: any) => x.sourceId === address.stateId),
                    selection: address.cityId ?? '',
                    next: "zone",
                });
                if (address.cityId) {
                    drawList.push({
                        list: zonesList.filter((x: any) => x.sourceId === address.cityId),
                        next: "select",
                        selection: address.zoneId ?? '',
                    });
                }
            }
        }
    }

    const onSelection = (path: any, address: any) => {
        let idKey = "zoneId";
        let nameKey = "zone";
        if (path.next === "city") {
            idKey = 'stateId';
            nameKey = 'state';
            dispatch(getCities(address._id));
        } else if (path.next === "zone") {
            idKey = 'cityId';
            nameKey = 'city';
            dispatch(getZones(address._id));
        } else {
            formik.setFieldValue('regionState', `${formik.values.state} / ${formik.values.city} / ${address.name}`);
        }
        formik.setFieldValue(idKey, address._id);
        formik.setFieldValue(nameKey, address.name);
        drawAddressSelection();
        sendUpdate();
    }

    const sendUpdate = () => {
        const updatedValues = { ...formik.values };
        updatedValues.coordinates = [updatedValues.longitude, updatedValues.latitude,]
        onAddressUpdate(updatedValues);
    }

    const sendUpdateCheckbox = (data: any) => {
        formik.values.isPlaza = data;
        formik.values.coordinates = [formik.values.longitude, formik.values.latitude]
        onAddressUpdate(formik.values);
    }

    return (
        <React.Fragment>

            <div className='d-grid gap-4'>


                <Col md={6} xl={6}>

                    <div className='d-grid gap-4'>
                        <div className='formContainer'>
                            <label className="form-label" htmlFor="password-input"><span style={{ color: 'red', display: 'inline' }}>*</span> Province</label>

                            <Input
                                type="select"
                                name="stateId"
                                className={`form-control ${formik.touched.stateId && formik.errors.stateId ? "is-invalid" : ""}`}
                                id="stateId"
                                value={formik.values.stateId}
                                onChange={(e) => {
                                    const selectedState = stateList.find((state: any) => state._id === e.target.value);
                                    if (selectedState) {
                                        formik.setFieldValue("stateId", selectedState._id);
                                        formik.setFieldValue("state", selectedState.name);
                                        formik.setFieldValue("cityId", "");
                                        formik.setFieldValue("zoneId", "");
                                        dispatch(getCities(selectedState._id));
                                    }
                                }}
                                data-testing-Id="regionInput_AdressFormBox"
                            >
                                <option value="">Select Province</option>
                                {stateList.map((state: any) => (
                                    <option key={state._id} value={state._id}>
                                        {state.name}
                                    </option>
                                ))}
                            </Input>
                            {formik.touched.stateId && formik.errors.stateId && (
                                <div className="invalid-feedback">{formik.errors.stateId}</div>
                            )}
                        </div>

                        <div className='formContainer'>
                        <label className="form-label " htmlFor="password-input"><span style={{ color: 'red', display: 'inline' }}>*</span> City</label>
                            {formik.values.stateId && (
                                <>
                                    <Input
                                        type="select"
                                        name="cityId"
                                        className={`form-control ${formik.touched.cityId && formik.errors.cityId ? "is-invalid" : ""}`}
                                        id="cityId"
                                        value={formik.values.cityId}
                                        onChange={(e) => {
                                            const selectedCity = citiesList.find((city: any) => city._id === e.target.value);
                                            if (selectedCity) {
                                                formik.setFieldValue("cityId", selectedCity._id);
                                                formik.setFieldValue("city", selectedCity.name);
                                                formik.setFieldValue("zoneId", "");
                                                dispatch(getZones(selectedCity._id));
                                            }
                                        }}
                                        disabled={!formik.values.stateId}
                                        data-testing-Id="cityInput_AdressFormBox"
                                    >
                                        <option value="">Select City</option>
                                        {citiesList
                                            .filter((city: any) => city.sourceId === formik.values.stateId)
                                            .map((city: any) => (
                                                <option key={city._id} value={city._id}>
                                                    {city.name}
                                                </option>
                                            ))}
                                    </Input>
                                    {formik.touched.cityId && formik.errors.cityId && (
                                        <div className="invalid-feedback">{formik.errors.cityId}</div>
                                    )}
                                </>
                            )}
                            {/*  */}
                            {/*  */}
                            {/*  */}
                        </div>


                        <div className='formContainer'>
                        <label className="form-label " htmlFor="password-input"><span style={{ color: 'red', display: 'inline' }}>*</span> Area</label>
                            {/* District Select - Show only if a city is selected */}
                            {formik.values.cityId && (
                                <>
                                    <Input
                                        type="select"
                                        name="zoneId"
                                        className={`form-control ${formik.touched.zoneId && formik.errors.zoneId ? "is-invalid" : ""}`}
                                        id="zoneId"
                                        value={formik.values.zoneId}
                                        onChange={(e) => {
                                            const selectedZone = zonesList.find((zone: any) => zone._id === e.target.value);
                                            if (selectedZone) {
                                                formik.setFieldValue("zoneId", selectedZone._id);
                                                formik.setFieldValue("zone", selectedZone.name);
                                            }
                                        }}
                                        disabled={!formik.values.cityId}
                                        data-testing-Id="districtInput_AdressFormBox"
                                    >
                                        <option value="">Select Area</option>
                                        {zonesList
                                            .filter((zone: any) => zone.sourceId === formik.values.cityId)
                                            .map((zone: any) => (
                                                <option key={zone._id} value={zone._id}>
                                                    {zone.name}
                                                </option>
                                            ))}
                                    </Input>
                                    {formik.touched.zoneId && formik.errors.zoneId && (
                                        <div className="invalid-feedback">{formik.errors.zoneId}</div>
                                    )}
                                </>
                            )}

                        </div>
                    </div>

                </Col>



                <div className="live-preview my-2">
                    <div className="form-check form-check-inline">
                        <Input
                            className="form-check-input"
                            type="radio"
                            name="isPlaza"
                            id="plaza"
                            value="plaza"
                            onChange={() => {
                                formik.setFieldValue("isPlaza", true);
                                sendUpdateCheckbox(true);
                                formik.setFieldValue("plazaMarketName", ""); // Clear the plaza name when switching
                            }}
                            checked={formik.values.isPlaza}
                            data-testing-Id="plazaOrMarketInput_AdressFormBox"
                        />
                        <Label className="form-check-label" htmlFor="plaza">
                           Plaza or market
                        </Label>
                    </div>

                    <div className="form-check form-check-inline">
                        <Input
                            className="form-check-input"
                            type="radio"
                            name="isPlaza"
                            id="individualShop"
                            value="individualShop"
                            onChange={() => {
                                formik.setFieldValue("isPlaza", false);
                                sendUpdateCheckbox(false);
                            }}
                            checked={!formik.values.isPlaza}
                            data-testing-Id="individualShopInput_AdressFormBox"
                        />
                        <Label className="form-check-label" htmlFor="individualShop">
                            Individual Shop
                        </Label>
                    </div>
                </div>

                {formik.values.isPlaza && (
                    <Col md={6} xl={6}>
                         <div className="formContainer">     
                         <label className="form-label" htmlFor="password-input"><span style={{ color: 'red', display: 'inline' }}>*</span> Plaza Market Name</label>
                        <Input
                            type="text"
                            name="plazaMarketName"
                            className={`form-control ${formik.touched.plazaMarketName && formik.errors.plazaMarketName ? "is-invalid" : ""
                                }`}
                            id="plazaMarketName"
                            placeholder="Plaza Market Name"
                            onChange={(e) => {
                                formik.handleChange(e);
                                sendUpdate();
                            }}
                            onBlur={formik.handleBlur}
                            value={formik.values.plazaMarketName}
                            data-testing-Id="plazeMarketNameInput_AdressFormBox"
                        />
                        {formik.touched.plazaMarketName && formik.errors.plazaMarketName && (
                            <div className="invalid-feedback">{formik.errors.plazaMarketName}</div>
                        )}
                        </div>
                    </Col>
                )}

                <Col md={6} xl={6}>
                <div className="formContainer">     
                         <label className="form-label" htmlFor="password-input"><span style={{ color: 'red', display: 'inline' }}>*</span> Shop Name</label>
                    <Input
                        type="text"
                        name="shopName"
                        className={`form-control ${formik.touched.shopName && formik.errors.shopName ? "is-invalid" : ""
                            }`}
                        id="shopName"
                        placeholder="Shop Name"
                        onChange={(e) => {
                            formik.handleChange(e);
                            sendUpdate();
                        }}
                        onBlur={formik.handleBlur}
                        value={formik.values.shopName}
                        data-testing-Id="shopNameInput_AdressFormBox"
                    />
                    {formik.touched.shopName && formik.errors.shopName && (
                        <div className="invalid-feedback">{formik.errors.shopName}</div>
                    )}
                    </div>
                </Col>

                <Col md={6} xl={6}>
                <div className="formContainer">     
                <label className="form-label" htmlFor="password-input"><span style={{ color: 'red', display: 'inline' }}>*</span> Shop Number</label>
                    <Input
                        type="text"
                        name="shopNumber"
                        className={`form-control ${formik.touched.shopNumber && formik.errors.shopNumber ? "is-invalid" : ""
                            }`}
                        id="shopNumber"
                        placeholder="Shop Number"
                        onChange={(e) => {
                            formik.handleChange(e);
                            sendUpdate();
                        }}
                        onBlur={formik.handleBlur}
                        value={formik.values.shopNumber}
                        data-testing-Id="shopNumberInput_AdressFormBox"
                    />
                    {formik.touched.shopNumber && formik.errors.shopNumber && (
                        <div className="invalid-feedback">{formik.errors.shopNumber}</div>
                    )}
                    </div>
                </Col>

                <Col md={6} xl={6}>
                <div className="formContainer">     
                <label className="form-label" htmlFor="password-input"><span style={{ color: 'red', display: 'inline' }}>*</span> Shop Address</label>
                    <Input
                        type="text"
                        name="streetAddress"
                        className={`form-control ${formik.touched.streetAddress && formik.errors.streetAddress ? "is-invalid" : ""
                            }`}
                        id="streetAddress"
                        placeholder="Shop Address"
                        onChange={(e) => {
                            formik.handleChange(e);
                            sendUpdate();
                        }}
                        onBlur={formik.handleBlur}
                        value={formik.values.streetAddress}
                        data-testing-Id="shopAdressInput_AdressFormBox"
                    />
                    {formik.touched.streetAddress && formik.errors.streetAddress && (
                        <div className="invalid-feedback">{formik.errors.streetAddress}</div>
                    )}
                    </div>
                </Col>

                <Col md={6} xl={6}>
                <div className="formContainer">     
                <label className="form-label" htmlFor="password-input"><span style={{ color: 'red', display: 'inline' }}>*</span> Latitude</label>
                    <Input
                        type="number"
                        name="latitude"
                        className={`form-control ${formik.touched.latitude && formik.errors.latitude ? "is-invalid" : ""
                            }`}
                        id="streetAddress"
                        placeholder="latitude"
                        onChange={(e) => {
                            formik.handleChange(e);
                            sendUpdate();
                        }}
                        onBlur={formik.handleBlur}
                        value={formik.values.latitude}
                    />
                    {formik.touched.latitude && formik.errors.latitude && (
                        <div className="invalid-feedback">{formik.errors.latitude}</div>
                    )}
                    </div>
                </Col>

                <Col md={6} xl={6}>
                <div className="formContainer">     
                <label className="form-label" htmlFor="password-input"><span style={{ color: 'red', display: 'inline' }}>*</span> Longitude</label>
                    <Input
                        type="number"
                        name="longitude"
                        className={`form-control ${formik.touched.longitude && formik.errors.longitude ? "is-invalid" : ""
                            }`}
                        id="longitude"
                        placeholder="longitude"
                        onChange={(e) => {
                            formik.handleChange(e);
                            sendUpdate();
                        }}
                        onBlur={formik.handleBlur}
                        value={formik.values.longitude}
                    />
                    {formik.touched.longitude && formik.errors.longitude && (
                        <div className="invalid-feedback">{formik.errors.longitude}</div>
                    )}
                    </div>
                </Col>

                <Col md={6} xl={6}>
                    {/* <TPLMapComponent coordinates={userCoordinates} onCoordinatesChange={handleCoordinatesChange} /> */}

                </Col>



            </div>




        </React.Fragment>
    );
};

export default AddressFormBox;